import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import { attachRouterErrorHandler } from "@/services/sessionRecovery";

// Layouts
import LayoutDashboard from "@/layouts/LayoutDashboard.vue";
import LayoutPublic from "@/layouts/LayoutPublic.vue";
import LayoutRecruit from "@/layouts/LayoutRecruit.vue";
import LayoutExternal from "@/layouts/LayoutExternal.vue";
import store from "@/store/index";
import jwtDecode from "jwt-decode";

const replaceHttpWithHttps = () => {
  if (location.hostname !== "localhost") {
    if (location.protocol !== "https:") {
      location.replace(
        `https:${location.href.substring(location.protocol.length)}`
      );
    }
  }
};

interface DecodedToken {
  exp?: number;
}

const hasValidToken = () => {
  const token = localStorage.getItem("user_token");

  if (!token) {
    return { isValid: false };
  }

  try {
    const decodedToken = jwtDecode(token) as DecodedToken;

    if (!decodedToken.exp) {
      return { isValid: false };
    }

    const expirationDate = new Date(decodedToken.exp * 1000);
    if (expirationDate > new Date()) {
      return { isValid: true, tokenData: decodedToken };
    } else {
      return { isValid: false };
    }
  } catch (error) {
    console.error("Error decoding token:", error);
    return { isValid: false };
  }
};

const dashboardGuard = async (to: any, from: any, next: any) => {
  replaceHttpWithHttps();

  const token = hasValidToken();
  if (!token.isValid) {
    store.dispatch("logout");
    next("/login");
  } else {
    next();
  }
};

const userIsOdinGuard = async (to: any, from: any, next: any) => {
  replaceHttpWithHttps();

  const token = hasValidToken();
  if (!token.isValid) {
    store.dispatch("logout");
    next("/login");
  } else {
    const isOdin = await store.dispatch("loggedInUserIsOdin");

    if (isOdin) {
      next();
    } else {
      next("/access-denied");
    }
  }
};

const userIsAdminGuard = async (to: any, from: any, next: any) => {
  replaceHttpWithHttps();

  const token = hasValidToken();
  if (!token.isValid) {
    store.dispatch("logout");
    next("/login");
  } else {
    const isAdmin = await store.dispatch("loggedInUserIsAdmin");

    if (isAdmin) {
      next();
    } else {
      next("/access-denied");
    }
  }
};
const userIsWriterGuard = async (to: any, from: any, next: any) => {
  replaceHttpWithHttps();

  const token = hasValidToken();
  if (!token.isValid) {
    store.dispatch("logout");
    next("/login");
  } else {
    const isAdmin = await store.dispatch("loggedInUserIsAdmin");
    const isWriter = await store.dispatch("loggedInUserIsWriter");

    if (isAdmin || isWriter) {
      next();
    } else {
      next("/access-denied");
    }
  }
};

const userIsPositionAdminGuard = async (to: any, from: any, next: any) => {
  replaceHttpWithHttps();

  const token = hasValidToken();
  if (!token.isValid) {
    store.dispatch("logout");
    next("/login");
  } else {
    const isAdmin = await store.dispatch(
      "loggedInUserIsPositionAdmin",
      parseInt(to.params.id)
    );

    if (isAdmin) {
      next();
    } else {
      next("/access-denied");
    }
  }
};
const userIsPositionWriterGuard = async (to: any, from: any, next: any) => {
  replaceHttpWithHttps();

  const token = hasValidToken();
  if (!token.isValid) {
    store.dispatch("logout");
    next("/login");
  } else {
    const isWriter = await store.dispatch(
      "loggedInUserIsPositionWriter",
      parseInt(to.params.id)
    );

    if (isWriter) {
      next();
    } else {
      next("/access-denied");
    }
  }
};

const loginGuard = (to: any, from: any, next: any) => {
  replaceHttpWithHttps();

  const token = hasValidToken();
  if (token.isValid) {
    next("/admin");
  } else {
    next();
  }
};
const routes: Array<RouteRecordRaw> = [
  {
    path: "/admin",
    name: "home",
    alias: "/",
    meta: { layout: LayoutDashboard },
    component: () => import("../features/shared/views/HomeView.vue"),
    beforeEnter: dashboardGuard,
  },

  {
    path: "/admin/pick-container",
    name: "pick-container",
    component: () => import("../features/shared/views/ContainerPickerView.vue"),
    beforeEnter: dashboardGuard,
  },
  {
    path: "/admin/users",
    name: "users",
    meta: { layout: LayoutDashboard },
    component: () => import("../features/users/views/UsersView.vue"),
    beforeEnter: userIsAdminGuard,
  },
  {
    path: "/admin/users/:id",
    name: "ProfileView",
    meta: { layout: LayoutDashboard },
    component: () => import("../features/users/views/ProfileView.vue"),
    beforeEnter: userIsAdminGuard,
  },
  {
    path: "/admin/users/new",
    name: "AddUserView",
    meta: { layout: LayoutDashboard },
    component: () => import("../features/users/views/AddUserView.vue"),
    beforeEnter: userIsAdminGuard,
  },
  {
    path: "/admin/positions/new",
    name: "formbuilderPicker",
    meta: { layout: LayoutDashboard },
    component: () => import("../features/positions/views/PositionNewView.vue"),
    beforeEnter: userIsWriterGuard,
  },
  {
    path: "/admin/positions/:action/:type",
    name: "formbuilderVuelidate",
    meta: { layout: LayoutDashboard },
    component: () =>
      import("../features/shared/views/FormBuilderVuelidate.vue"),
    beforeEnter: userIsWriterGuard,
  },
  {
    path: "/admin/templates/skills",
    name: "skillTemplates",
    meta: { layout: LayoutDashboard },
    component: () =>
      import("../features/templates/views/skills/SkillsTemplatesView.vue"),
    beforeEnter: userIsWriterGuard,
  },
  {
    path: "/admin/templates/email",
    name: "emailtemplatesPositionTypePicker",
    meta: { layout: LayoutDashboard },
    component: () =>
      import(
        "../features/templates/views/email/EmailTemplatesPositionTypeView.vue"
      ),
    beforeEnter: userIsWriterGuard,
  },
  {
    path: "/admin/templates/email/:positionTypeId",
    name: "emailtemplates",
    meta: { layout: LayoutDashboard },
    component: () =>
      import("../features/templates/views/email/EmailTemplatesView.vue"),
    beforeEnter: userIsWriterGuard,
  },
  {
    path: "/admin/integration",
    name: "admin_integration",
    meta: { layout: LayoutDashboard },
    component: () =>
      import("@/features/containerpartners/views/ContainerPartnersView.vue"),
    beforeEnter: userIsAdminGuard,
  },
  {
    path: "/admin/container/new",
    name: "containerNew",
    meta: { layout: LayoutDashboard },
    component: () =>
      import("../features/containers/views/ContainerNewView.vue"),
    beforeEnter: userIsOdinGuard,
  },
  {
    path: "/admin/container/profile",
    name: "containerProfile",
    meta: { layout: LayoutDashboard },
    component: () =>
      import("../features/containers/views/ContainerProfileView.vue"),
    beforeEnter: userIsAdminGuard,
  },
  // Billing routes
  {
    path: "/admin/billing-management",
    name: "billingManagement",
    meta: { layout: LayoutDashboard },
    component: () =>
      import("../features/admin/views/OdinBillingManagement.vue"),
    beforeEnter: userIsOdinGuard,
  },
  {
    path: "/admin/odin/customers",
    name: "odinCustomers",
    meta: { layout: LayoutDashboard },
    component: () => import("../features/admin/views/OdinCustomersView.vue"),
    beforeEnter: userIsOdinGuard,
  },
  {
    path: "/admin/positions",
    name: "positions",
    meta: { layout: LayoutDashboard },
    component: () => import("../features/positions/views/PositionsView.vue"),
    beforeEnter: dashboardGuard,
  },
  {
    path: "/admin/positions/:id",
    name: "position",
    meta: { layout: LayoutDashboard },
    component: () =>
      import("../features/positions/views/PositionDetailsView.vue"),
    props: intParams,
    beforeEnter: dashboardGuard,
  },
  {
    path: "/admin/positions/:id/edit",
    name: "editPosition",
    meta: { layout: LayoutDashboard },
    component: () => import("../features/shared/views/FormBuilderEditView.vue"),
    props: intParams,
    beforeEnter: userIsPositionWriterGuard,
  },
  {
    path: "/admin/positions/:id/permissions",
    name: "positionPermissions",
    meta: { layout: LayoutDashboard },
    component: () =>
      import("../features/positions/views/PositionPermissionsView.vue"),
    props: intParams,
    beforeEnter: userIsPositionAdminGuard,
  },
  {
    path: "/admin/positions/:id/applicants/:applicantId",
    name: "positionApplicant",
    meta: { layout: LayoutDashboard },
    component: () =>
      import("../features/positions/views/PositionApplicantView.vue"),
    props: intParams,
    beforeEnter: dashboardGuard,
  },

  {
    path: "/admin/positions/:id/applicants/new",
    name: "positionApplicantNew",
    meta: { layout: LayoutDashboard },
    component: () =>
      import("../features/positions/views/PositionApplicantCreateView.vue"),
    props: intParams,
    beforeEnter: dashboardGuard,
  },
  {
    path: "/admin/positions/:id/integrations",
    name: "postionIntegrations",
    meta: { layout: LayoutDashboard },
    component: () =>
      import("../features/containerpartners/views/PositionPartnersView.vue"),
    props: intParams,
    beforeEnter: dashboardGuard,
  },

  {
    path: "/admin/gdpr",
    name: "GDPR",
    meta: { layout: LayoutDashboard },
    component: () => import("../features/gdpr/views/ActionTypePickerView.vue"),
    beforeEnter: userIsAdminGuard,
  },
  {
    path: "/admin/gdpr/inform",
    name: "InformUser",
    meta: { layout: LayoutDashboard },
    component: () => import("../features/gdpr/views/InformUser.vue"),
    beforeEnter: userIsAdminGuard,
  },
  {
    path: "/admin/gdpr/delete",
    name: "ForgetUser",
    meta: { layout: LayoutDashboard },
    component: () => import("../features/gdpr/views/ForgetUser.vue"),
    beforeEnter: userIsAdminGuard,
  },
  {
    path: "/admin/banners",
    name: "PositionBanners",
    meta: { layout: LayoutDashboard },
    component: () => import("../features/positions/views/Banners.vue"),
    beforeEnter: userIsWriterGuard,
  },
  {
    path: "/admin/form-templates",
    name: "PositionFormTemplates",
    meta: { layout: LayoutDashboard },
    component: () =>
      import("../features/templates/views/FormTemplatesView.vue"),
    beforeEnter: userIsWriterGuard,
  },
  {
    path: "/admin/form-templates/new",
    name: "CreatePositionFormTemplate",
    meta: { layout: LayoutDashboard },
    component: () => import("../features/templates/views/FormTemplateView.vue"),
    beforeEnter: userIsWriterGuard,
  },
  {
    path: "/admin/notifications/:id",
    name: "notificationItem",
    meta: { layout: LayoutDashboard },
    component: () =>
      import("../features/notifications/views/NotificationItemView.vue"),
    beforeEnter: userIsWriterGuard,
  },
  {
    path: "/admin/form-templates/edit/:id",
    name: "EditPositionFormTemplate",
    meta: { layout: LayoutDashboard },
    component: () => import("../features/templates/views/FormTemplateView.vue"),
    beforeEnter: userIsWriterGuard,
  },
  {
    path: "/admin/generate-data",
    name: "GenerateData",
    meta: { layout: LayoutDashboard },
    component: () =>
      import("../features/shared/views/GenerateContainerAndPositions.vue"),
    beforeEnter: userIsOdinGuard,
  },
  {
    path: "/admin/access-denied",
    name: "access-denied",
    meta: { layout: LayoutDashboard },
    component: () => import("../features/shared/views/AccessDenied.vue"),
    beforeEnter: dashboardGuard,
  },
  {
    path: "/admin/news",
    name: "NewsView",
    meta: { layout: LayoutDashboard },
    component: () => import("../features/news/views/NewsView.vue"),
    beforeEnter: dashboardGuard,
  },
  {
    path: "/admin/news/:id",
    name: "CardDetails",
    meta: { layout: LayoutDashboard },
    component: () => import("../features/news/views/CardDetailsView.vue"),
    beforeEnter: dashboardGuard,
    props: true,
  },
  {
    path: "/admin/news/:id/edit",
    name: "EditNewsPost",
    meta: { layout: LayoutDashboard },
    component: () => import("../features/news/views/EditNewsPost.vue"),
    beforeEnter: dashboardGuard,
  },
  {
    path: "/admin/create-news",
    name: "WriteNewNews",
    meta: { layout: LayoutDashboard },
    component: () => import("../features/news/views/WriteNewNews.vue"),
    beforeEnter: userIsOdinGuard,
  },
  {
    path: "/admin/create-news/details",
    name: "NewCardDetailsView",
    meta: { layout: LayoutDashboard },
    component: () => import("../features/news/views/NewCardDetailsView.vue"),
    beforeEnter: userIsOdinGuard,
  },
  {
    path: "/admin/recruit",
    name: "RecruitHome",
    meta: { layout: LayoutRecruit },
    component: () => import("../features/recruits/views/RecruitHomeView.vue"),
  },
  {
    path: "/admin/minasidor",
    name: "MyProfile",
    meta: { layout: LayoutRecruit },
    component: () => import("../features/recruits/views/MyProfile.vue"),
  },
  {
    path: "/admin/recruit-chat",
    name: "RecruitChat",
    meta: { layout: LayoutRecruit },
    component: () => import("../features/recruits/views/ChatView.vue"),
  },
  {
    path: "/admin/events",
    name: "Events",
    meta: { layout: LayoutDashboard },
    component: () => import("../features/messaging/views/IndexPage.vue"),
    beforeEnter: dashboardGuard,
  },
  {
    path: "/admin/recruit-chat",
    name: "RecruitChat",
    meta: { layout: LayoutDashboard },
    component: () => import("../features/recruits/views/ChatView.vue"),
    beforeEnter: dashboardGuard,
  },
  {
    path: "/admin/chat",
    name: "AdminChatPicker",
    meta: { layout: LayoutDashboard },
    component: () =>
      import("../features/chats/components/AdminPickPositionChat.vue"),
    beforeEnter: dashboardGuard,
  },
  {
    path: "/admin/chat/:id",
    name: "AdminChat",
    meta: { layout: LayoutDashboard },
    component: () =>
      import("../features/chats/components/AdminChatPositionView.vue"),
    beforeEnter: dashboardGuard,
  },
  {
    path: "/login",
    name: "login",
    component: () => import("@/features/accounts/views/LoginView.vue"),
    beforeEnter: loginGuard,
  },
  {
    path: "/forgot/:email?",
    name: "forgot",
    component: () =>
      import("../features/accounts/views/ForgotPasswordView.vue"),
    beforeEnter: loginGuard,
  },
  {
    path: "/admin/change-password",
    name: "change-password",
    meta: { layout: LayoutDashboard },
    component: () => import("../features/shared/views/SettingsView.vue"),
    beforeEnter: dashboardGuard,
  },
  {
    path: "/admin/profile",
    name: "userNotificationPreferences",
    meta: { layout: LayoutDashboard },
    component: () => import("../features/users/views/UserEditProfileView.vue"),
    beforeEnter: dashboardGuard,
  },

  {
    path: "/positions",
    name: "publicPositions",
    meta: { layout: LayoutPublic },
    component: () => import("@/features/public/views/PositionsView.vue"),
  },
  {
    path: "/:id?_:slug?",
    name: "publicPosition",
    meta: { layout: LayoutPublic },
    component: () => import("@/features/public/views/PositionView.vue"),
  },
  {
    path: "/:id?_:slug?/apply",
    name: "publicPositionApply",
    meta: { layout: LayoutPublic },
    component: () => import("@/features/public/views/PositionApplyView.vue"),
  },
  {
    // ANSKNI-107/108: external client (Bolag B) shortlist.
    path: "/extern",
    name: "ExternalShortlist",
    meta: { layout: LayoutExternal },
    component: () =>
      import("../features/external/views/ExternalShortlistView.vue"),
    beforeEnter: dashboardGuard,
  },
  {
    // ANSKNI-107: stripped external applicant detail.
    path: "/extern/kandidat/:id",
    name: "ExternalApplicant",
    meta: { layout: LayoutExternal },
    component: () =>
      import("../features/external/views/ExternalApplicantView.vue"),
    beforeEnter: dashboardGuard,
  },
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    meta: { layout: LayoutDashboard },
    component: () => import("../features/shared/views/NotFoundView.vue"),
    beforeEnter: dashboardGuard,
  },
];
function intParams(route: any) {
  return {
    id: Number(route.params.id),
  };
}

const router = createRouter({
  history: createWebHistory(),
  routes,
});

attachRouterErrorHandler(router);

// ANSKNI-108: lock external clients (Bolag B) to the /extern subtree. The role
// claim is read straight from the stored JWT (same source as hasValidToken), so
// it does not depend on store hydration timing. The backend is the real access
// gate — this only keeps an external user out of the admin UI.
const isExternalViewerToken = (): boolean => {
  try {
    const token = localStorage.getItem("user_token");
    if (!token) return false;
    const decoded = jwtDecode(token) as { role?: string };
    return (
      typeof decoded?.role === "string" &&
      decoded.role.includes("ExternalViewer")
    );
  } catch {
    return false;
  }
};

router.beforeEach((to, from, next) => {
  const path = to.path || "";
  const allowed =
    path.startsWith("/extern") ||
    path.startsWith("/login") ||
    path.startsWith("/access-denied");
  if (isExternalViewerToken() && !allowed) {
    next("/extern");
  } else {
    next();
  }
});

export default router;
